import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import i18next from "src/i18n/config";
import { RootState } from "src/store";
import { useCurrentWorkspaceEvents } from "src/store/api/events/helpers";
import { EventLevelHeader } from "src/ui/shared/table/headers/EventLevelHeader";
import { makeStyles } from "tss-react/mui";

import { showDetails } from "@dashboard/devices/store/index";
import { Device, EventReport } from "@dashboard/devices/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { selectLicensesEntities } from "@dashboard/licenses/store/selectors";
import { EventContentResult, renderEventContent } from "@dashboard/shared/components/events";
import { Box, Collapse, IconButton, TableCell, TableRow, Theme, Typography, useTheme } from "@mui/material";
import { useFetchBuildsForProductsQuery } from "@products/api";
import { useCurrentWorkspaceProducts } from "@products/api/helpers";
import TimeService from "@services/timeService/timeService";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";
import { Tooltip } from "@shared/Tooltip";
import {
    ColumnDef,
    ColumnFiltersState,
    ExpandedState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { useDeviceNameClickHandler } from "../../shared/EventTab/EventTab.utils";
import { PaneElement } from "../../shared/PaneElement";

const useStyles = makeStyles()((muiTheme: Theme) => ({
    expandedContent: {
        display: "flex",
        flexDirection: "column",
        gap: muiTheme.spacing(2),
        padding: muiTheme.spacing(2),
        paddingLeft: muiTheme.spacing(4),
    },
}));

const createColumns = (getEventDetails: (event: EventReport) => EventContentResult): ColumnDef<EventReport, unknown>[] => [
    {
        id: "expander",
        header: () => null,
        cell: ({ row }) => (
            <MinimalTableCell>
                <IconButton
                    size="large"
                    onClick={(e) => {
                        e.stopPropagation();
                        row.toggleExpanded();
                    }}
                >
                    {row.getIsExpanded() ? (
                        <i className="fa-solid fa-chevron-down" style={{ fontSize: 16 }} />
                    ) : (
                        <i className="fa-solid fa-chevron-right" style={{ fontSize: 16 }} />
                    )}
                </IconButton>
            </MinimalTableCell>
        ),
        enableSorting: false,
    },
    {
        header: i18next.t("sidepanels:devicePanel.table.header.date"),
        accessorKey: "created",
        enableSorting: true,
        cell: (info) => {
            const dateTime = TimeService.getLocalDate(info.row.original.created, "ISO", "dateTime");
            const date = TimeService.getLocalDate(info.row.original.created, "ISO", "date");
            return (
                <MinimalTableCell>
                    <Tooltip title={dateTime}>
                        <Typography>{date}</Typography>
                    </Tooltip>
                </MinimalTableCell>
            );
        },
    },
    {
        id: "eventLevel",
        header: () => i18next.t("common:label.type"),
        accessorFn: (row) => {
            return row.level;
        },
        filterFn: (row, _columnId, filterValue: string[]) => {
            return filterValue.includes(String(row.original.level));
        },
        cell: ({ row }) => {
            const level =
                row.original.level === "warning"
                    ? i18next.t("common:table.filter.warning")
                    : i18next.t("common:table.filter.notice");
            return (
                <MinimalTableCell>
                    <Typography variant="body1">{level}</Typography>
                </MinimalTableCell>
            );
        },
        enableSorting: false,
    },
    {
        header: i18next.t("common:label.event"),
        accessorKey: "eventTypeName",
        enableSorting: true,
        cell: (info) => {
            const eventDetails = getEventDetails(info.row.original);
            return (
                <MinimalTableCell>
                    <Typography variant="body1">{eventDetails.header}</Typography>
                </MinimalTableCell>
            );
        },
    },
];

interface Props {
    device: Device;
}

const EventTab = ({ device }: Props) => {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [expanded, setExpanded] = React.useState<ExpandedState>({});

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels"]);
    const events = useCurrentWorkspaceEvents({
        entityId: device.id.toString(),
        category: "device",
    });
    const { data: eventReports, isLoading } = events;
    useQueryErrorToast(events);
    const data = React.useMemo(() => eventReports?.list || [], [eventReports?.list]);
    const theme = useTheme();
    const licensesEntities = useSelector((state: RootState) => selectLicensesEntities(state));
    const allGroups = useSelector(selectGroups);

    const { data: products = [] } = useCurrentWorkspaceProducts();
    const { data: builds = [] } = useFetchBuildsForProductsQuery(products.map((product) => product.id));

    const handleDeviceNameClick = useDeviceNameClickHandler({
        devices: [device],
        allGroups,
        licensesEntities,
    });

    const handleGroupNameClick = React.useCallback(
        (event: EventReport) => {
            const group = allGroups.find((g) => g.id === event.groupId);
            if (group) {
                dispatch(
                    showDetails({
                        type: "group",
                        selectedId: group.id,
                    }),
                );
            }
        },
        [dispatch, allGroups],
    );

    const getEventDetails = (event: EventReport) => {
        const isLicenseEvent = event.eventTypeName.startsWith("LicenseEvent.");
        const license = isLicenseEvent
            ? licensesEntities[parseInt(event.entityId)]
            : device.licenseId
              ? licensesEntities[device.licenseId]
              : undefined;
        const group =
            allGroups.find((g) => g.id === event.groupId) ||
            (device.licenseId ? allGroups.find((g) => g.licenseIds.includes(device.licenseId)) : undefined);

        const eventDevice = isLicenseEvent ? license?.device : event.entityId === device.id.toString() ? device : undefined;
        const handleDeviceNameClickForEvent = eventDevice && eventDevice.id === device.id ? undefined : handleDeviceNameClick;

        const handleLicenseClick = license
            ? () => {
                  dispatch(
                      showDetails({
                          type: "license",
                          selectedId: license.id,
                      }),
                  );
              }
            : undefined;

        return renderEventContent(
            event,
            {
                license,
                device,
                navigate,
                group,
                allGroups,
                products,
                builds,
                handleDeviceNameClick: handleDeviceNameClickForEvent,
                handleGroupNameClick,
                handleLicenseClick,
            },
            theme,
        );
    };

    const columns = createColumns(getEventDetails);

    const table = useReactTable<EventReport>({
        data,
        columns,
        state: { columnFilters, expanded },
        onExpandedChange: setExpanded,
        getRowCanExpand: () => true,
        getSubRows: () => undefined,
        initialState: {
            sorting: [{ id: "created", desc: true }],
            pagination: {
                pageSize: 10,
                pageIndex: 0,
            },
        },
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const handleResetFilter = () => {
        setColumnFilters([]);
        setExpanded({});
        table.setPageIndex(0);
    };

    const { pageSize, pageIndex } = table.getState().pagination;

    return (
        <PaneElement.Container>
            <PaneElement.Header>{t("sidepanels:devicePanel.eventsTab.deviceEvents")}</PaneElement.Header>
            <PaneElement.TableLayout
                contentDeps={[pageIndex, pageSize, table.getFilteredRowModel().rows.length]}
                pagination={
                    <CustomTablePagination
                        component="div"
                        rowsPerPageOptions={[10, 24, 48]}
                        count={table.getFilteredRowModel().rows.length}
                        rowsPerPage={pageSize}
                        page={pageIndex}
                        onPageChange={(_event, page) => table.setPageIndex(page)}
                        onRowsPerPageChange={(e) => {
                            const size = e.target.value ? Number(e.target.value) : 10;
                            table.setPageSize(size);
                        }}
                    />
                }
            >
                <MinimalTable>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header}>
                                        {header.id === "eventLevel" && (
                                            <EventLevelHeader columnFilters={columnFilters} setColumnFilters={setColumnFilters} />
                                        )}
                                    </Header>
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody
                        table={table}
                        filter={{ isActive: !!columnFilters.length, onResetFilter: handleResetFilter }}
                        loading={isLoading}
                        noDataMessage={t("sidepanels:devicePanel.table.noEvents")}
                    >
                        {table.getRowModel().rows.map((row) => {
                            const eventDetails = getEventDetails(row.original);
                            return (
                                <React.Fragment key={row.id}>
                                    <TableRow>
                                        {row
                                            .getVisibleCells()
                                            .map((cell) =>
                                                flexRender(cell.column.columnDef.cell, { ...cell.getContext(), key: cell.id }),
                                            )}
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={columns.length} style={{ borderBottom: "none", padding: 0 }}>
                                            <Collapse in={row.getIsExpanded()} timeout="auto" unmountOnExit>
                                                <Box className={classes.expandedContent}>
                                                    <Typography variant="body1" component="div">
                                                        {eventDetails.component}
                                                    </Typography>
                                                    {eventDetails.details}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </MinimalTable>
            </PaneElement.TableLayout>
        </PaneElement.Container>
    );
};

export default EventTab;
